/**
 * AGENT REFERRALS (2026-11-01).
 *
 * One row per referred LOCATION — a business with two shops earns twice, and the user's commission is per
 * location, so flattening them would misstate the total.
 */
import React, { useMemo, useState } from "react";
import {
  AgentPageState,
  Badge,
  formatDate,
  money,
  styles,
  subscriptionTone,
  useAgentResource,
} from "./agentShared";

export default function AgentReferrals() {
  const { loading, error, unavailable, data, reload } = useAgentResource(
    "/api/platform-plans/referral/referrals",
  );
  const [search, setSearch] = useState("");

  const rows = useMemo(() => {
    const list = Array.isArray(data?.referrals) ? data.referrals : [];
    const needle = search.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((row) =>
      String(row.businessName || "").toLowerCase().includes(needle),
    );
  }, [data, search]);

  return (
    <AgentPageState
      loading={loading}
      error={error}
      unavailable={unavailable}
      reload={reload}
    >
      <div style={styles.page}>
        <h1 style={styles.h1}>Referrals</h1>
        <p style={{ ...styles.muted, marginTop: 0 }}>
          Every business that signed up with your code, and what it has earned you.
        </p>

        <div style={styles.card}>
          <div style={styles.toolbar}>
            <input
              type="search"
              placeholder="Search by business name"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={styles.input}
              aria-label="Search referrals"
            />
            <span style={styles.muted}>
              {rows.length} of {data?.count ?? 0}
            </span>
          </div>

          {rows.length === 0 ? (
            <div style={styles.empty}>
              {data?.count
                ? "No business matches that search."
                : "No referrals yet. Share your link and the businesses that join will appear here."}
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Business</th>
                    <th style={styles.th}>Subscription</th>
                    <th style={styles.th}>Rate</th>
                    <th style={styles.th}>Last month</th>
                    <th style={styles.th}>Earned</th>
                    <th style={styles.th}>Joined</th>
                    <th style={styles.th}>Commission ends</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td style={styles.td}>
                        <strong>{row.businessName}</strong>
                        {row.locationId ? (
                          <div style={styles.muted}>Location {row.locationId.slice(-6)}</div>
                        ) : null}
                      </td>
                      <td style={styles.td}>
                        <Badge tone={subscriptionTone(row.status)} />
                      </td>
                      <td style={styles.td}>{row.commissionPercent}%</td>
                      <td style={styles.td}>{money(row.lastCommissionCents)}</td>
                      <td style={styles.td}>
                        <strong>{money(row.totalCommissionCents)}</strong>
                        <div style={styles.muted}>
                          {row.commissionPayments || 0} payment
                          {row.commissionPayments === 1 ? "" : "s"}
                        </div>
                      </td>
                      <td style={styles.td}>{formatDate(row.attributedAt)}</td>
                      <td style={styles.td}>
                        {row.commissionEndsAt ? formatDate(row.commissionEndsAt) : "Ongoing"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AgentPageState>
  );
}
